import { useCallback, useEffect, useState } from 'react';
import {
  fetchCases, createCase, updateCase, assessVigilance,
  fetchCaseEvents, addCaseEvent, raiseNonconformance, fetchNcRef,
  fetchFollowers, addFollower, removeFollower, createFollowupCase, notifyCase,
  CASE_KINDS, CASE_SEVERITIES, CASE_STATUSES, CASE_SOURCES,
  type SupportCase, type CaseEvent, type Follower,
} from '../lib/support';
import { fetchMembers, type Member } from '../lib/projects';
import { fetchAccounts, type Account } from '../lib/customers';
import { fetchProducts, fetchStock, fetchOrders,
  type Product, type StockRow, type Order } from '../lib/trade';
import { onFail } from '../lib/errors';
import './support.css';

/* =====================================================================================
   Support: complaints, non-conformities, feature requests and tech support, each a
   numbered case with its timeline. Escalation happened the moment the case was created
   (the database trigger told Chorus); this screen is where the record is kept honest:
   acknowledge it, work it, write the reply the customer sees or the note they never do,
   decide a complaint's reportability, and raise the linked nonconformance in Quality
   when the case deserves one.
   ===================================================================================== */

const fmtDT = (s: string | null | undefined) => (s ? new Date(s).toLocaleString('en-GB',
  { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const KIND_PILL: Record<string, string> = {
  complaint: 'warn', non_conformity: 'warn', feature_request: 'info', tech_support: 'mute',
};
const STATUS_PILL: Record<string, string> = {
  new: 'warn', acknowledged: 'info', in_progress: 'info', waiting_customer: 'mute',
  resolved: 'ok', closed: 'mute',
};
const label = (list: [string, string][], k: string) => list.find(([x]) => x === k)?.[1] ?? k;

export function SupportView() {
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stock, setStock] = useState<StockRow[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [statusFilter, setStatusFilter] = useState<'open' | 'all' | 'closed'>('open');
  const [kindFilter, setKindFilter] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchCases().then(setCases).catch(onFail('The case list', () => setCases([])));
    fetchAccounts().then(setAccounts).catch(() => setAccounts([]));
    fetchProducts().then(setProducts).catch(() => setProducts([]));
    fetchStock().then(setStock).catch(() => setStock([]));
    fetchOrders().then(setOrders).catch(() => setOrders([]));
    fetchMembers().then(setMembers).catch(() => setMembers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = cases.find((c) => c.id === openId) ?? null;
  const accountName = (id: string | null) => accounts.find((a) => a.id === id)?.name ?? '';
  const memberName = (id: string | null) => members.find((m) => m.id === id)?.full_name ?? '';
  const visible = cases.filter((c) => {
    if (statusFilter === 'open' && ['resolved', 'closed'].includes(c.status)) return false;
    if (statusFilter === 'closed' && !['resolved', 'closed'].includes(c.status)) return false;
    if (kindFilter && c.kind !== kindFilter) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = [c.number, c.title, c.description, accountName(c.account_id),
        c.contact_name, c.contact_email, c.resolution].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
  const openCases = cases.filter((c) => !['resolved', 'closed'].includes(c.status));
  const dash = {
    open: openCases.length,
    unacknowledged: openCases.filter((c) => c.status === 'new').length,
    complaints: openCases.filter((c) => ['complaint', 'non_conformity'].includes(c.kind)).length,
    waiting: openCases.filter((c) => c.status === 'waiting_customer').length,
    resolved30: cases.filter((c) => c.resolved_at
      && Date.now() - new Date(c.resolved_at).getTime() < 30 * 864e5).length,
  };

  return (
    <div className="page support">
      <div className="page-head">
        <h1 className="page-title">Support</h1>
        <p className="page-sub">
          Every complaint, non-conformity, feature request and support question as a
          numbered case with its full record. Chorus was told the moment each one arrived;
          complaints carry their reportability decision and can raise a linked
          nonconformance in Quality.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      <div className="metrics su-dash">
        <div className="metric card"><div className="label">Open cases</div>
          <div className="metric-value">{dash.open}</div></div>
        <div className="metric card"><div className="label">Unacknowledged</div>
          <div className="metric-value" style={dash.unacknowledged ? { color: 'var(--risk)' } : undefined}>{dash.unacknowledged}</div></div>
        <div className="metric card"><div className="label">Open complaints / NC</div>
          <div className="metric-value">{dash.complaints}</div></div>
        <div className="metric card"><div className="label">Waiting on customer</div>
          <div className="metric-value">{dash.waiting}</div></div>
        <div className="metric card"><div className="label">Resolved, last 30 days</div>
          <div className="metric-value">{dash.resolved30}</div></div>
      </div>

      <div className="toolbar">
        <input className="input su-search" placeholder="Search number, title, account, contact, text…"
               value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="su-filters">
        {(['open', 'all', 'closed'] as const).map((f) => (
          <button key={f} className={`tab ${statusFilter === f ? 'active' : ''}`}
                  onClick={() => setStatusFilter(f)}>
            {f === 'open' ? 'Open' : f === 'all' ? 'Everything' : 'Resolved and closed'}
          </button>
        ))}
        <span className="su-gap" />
        <button className={`tab ${kindFilter === '' ? 'active' : ''}`} onClick={() => setKindFilter('')}>All kinds</button>
        {CASE_KINDS.map(([k, l]) => (
          <button key={k} className={`tab ${kindFilter === k ? 'active' : ''}`}
                  onClick={() => setKindFilter(kindFilter === k ? '' : k)}>{l}</button>
        ))}
      </div>

      <div className="toolbar">
        <button className="btn primary" onClick={() => setCreating(true)}>Log a case</button>
      </div>

      <section className="card">
        {visible.length === 0 && (
          <p className="module-empty">
            {statusFilter === 'open' ? 'Nothing open. The portal, the phone and the field will change that.' : 'Nothing matches.'}
          </p>
        )}
        {visible.map((c) => (
          <div key={c.id} className="su-row" onClick={() => setOpenId(c.id)}>
            <div>
              <strong>{c.number}</strong> · {c.title}
              <span className={`pill ${KIND_PILL[c.kind] ?? 'mute'}`}>{label(CASE_KINDS, c.kind)}</span>
              <span className={`pill ${STATUS_PILL[c.status] ?? 'mute'}`}>{label(CASE_STATUSES, c.status)}</span>
              {c.severity !== 'normal' && <span className={`pill ${c.severity === 'low' ? 'mute' : 'warn'}`}>{c.severity}</span>}
              {c.nonconformance_id && <span className="pill info">NC raised</span>}
              <div className="cell-sub">
                {accountName(c.account_id) || c.contact_name || 'No account'}
                {' '}· {c.source.replace('_', ' ')} · opened {fmtDT(c.opened_at)}
                {c.created_by && <> by {memberName(c.created_by)}</>}
                {' '}· {c.assigned_to ? `assigned to ${memberName(c.assigned_to)}` : 'unassigned'}
              </div>
            </div>
            <span className="su-go">Open</span>
          </div>
        ))}
      </section>

      {creating && (
        <NewCase accounts={accounts} products={products} stock={stock} orders={orders}
          onClose={() => setCreating(false)}
          onDone={(n) => { setCreating(false); setMsg(`${n} opened; the right people have been told.`); load(); }} />
      )}
      {open && (
        <CaseDrawer c={open} accountName={accountName(open.account_id)}
          products={products} stock={stock} members={members} memberName={memberName}
          allCases={cases}
          onOpenCase={(id) => setOpenId(id)}
          onClose={() => setOpenId(null)} onChanged={load} onMsg={setMsg} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ new case */
function NewCase({ accounts, products, stock, orders, onClose, onDone }: {
  accounts: Account[]; products: Product[]; stock: StockRow[]; orders: Order[];
  onClose: () => void; onDone: (n: string) => void;
}) {
  const [f, setF] = useState({ kind: 'tech_support', severity: 'normal', source: 'phone',
    accountId: '', contactName: '', contactEmail: '', title: '', description: '',
    productId: '', lotId: '', orderId: '' });
  const [accountText, setAccountText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const accountOrders = orders.filter((o) => o.account_id === f.accountId);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer su-drawer">
        <h2>Log a case</h2>
        {err && <p className="block-note">{err}</p>}
        <div className="su-two">
          <label className="cf-field"><span>Kind</span>
            <select className="input sel" value={f.kind} onChange={set('kind')}>
              {CASE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label className="cf-field"><span>Severity</span>
            <select className="input sel" value={f.severity} onChange={set('severity')}>
              {CASE_SEVERITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
        </div>
        <div className="su-two">
          <label className="cf-field"><span>How it arrived</span>
            <select className="input sel" value={f.source} onChange={set('source')}>
              {CASE_SOURCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label className="cf-field"><span>Customer account</span>
            <input className="input" list="su-accounts" placeholder="Start typing the account name…"
                   value={accountText}
                   onChange={(e) => {
                     const v = e.target.value; setAccountText(v);
                     const hit = accounts.find((a) => a.name === v);
                     setF((prev) => ({ ...prev, accountId: hit ? hit.id : '' }));
                   }} />
            <datalist id="su-accounts">
              {accounts.map((a) => <option key={a.id} value={a.name} />)}
            </datalist></label>
        </div>
        <div className="su-two">
          <label className="cf-field"><span>Contact name</span>
            <input className="input" value={f.contactName} onChange={set('contactName')} /></label>
          <label className="cf-field"><span>Contact email</span>
            <input className="input" type="email" value={f.contactEmail} onChange={set('contactEmail')} /></label>
        </div>
        <label className="cf-field"><span>Title</span>
          <input className="input" value={f.title} onChange={set('title')} /></label>
        <label className="cf-field"><span>What happened</span>
          <textarea className="input" rows={3} value={f.description} onChange={set('description')} /></label>
        <h3 className="su-h3">Links (as applicable)</h3>
        <div className="su-two">
          <label className="cf-field"><span>Product</span>
            <select className="input sel" value={f.productId} onChange={set('productId')}>
              <option value="">None</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
            </select></label>
          <label className="cf-field"><span>Lot</span>
            <select className="input sel" value={f.lotId} onChange={set('lotId')}>
              <option value="">None</option>
              {stock.filter((s) => !f.productId || s.product_id === f.productId)
                .map((s) => <option key={s.lot_id} value={s.lot_id}>{s.sku} lot {s.lot_number}</option>)}
            </select></label>
        </div>
        <label className="cf-field"><span>Order</span>
          <select className="input sel" value={f.orderId} onChange={set('orderId')} disabled={!f.accountId}>
            <option value="">None</option>
            {accountOrders.map((o) => <option key={o.id} value={o.id}>{o.number}</option>)}
          </select></label>
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            setErr(null);
            try {
              if (!f.title.trim()) throw new Error('A title, so the list reads.');
              const n = await createCase({ kind: f.kind, severity: f.severity,
                title: f.title.trim(), description: f.description || undefined,
                source: f.source, accountId: f.accountId || undefined,
                contactName: f.contactName || undefined, contactEmail: f.contactEmail || undefined,
                productId: f.productId || undefined, lotId: f.lotId || undefined,
                orderId: f.orderId || undefined });
              onDone(n);
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Open the case</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ case drawer */
function CaseDrawer({ c, accountName, products, stock, members, memberName, allCases, onOpenCase, onClose, onChanged, onMsg }: {
  c: SupportCase; accountName: string; products: Product[]; stock: StockRow[];
  members: Member[]; memberName: (id: string | null) => string;
  allCases: SupportCase[]; onOpenCase: (id: string) => void;
  onClose: () => void; onChanged: () => void; onMsg: (s: string) => void;
}) {
  const closed = c.status === 'closed';
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [ncRef, setNcRef] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [toCustomer, setToCustomer] = useState(false);
  const [resolution, setResolution] = useState(c.resolution ?? '');
  const [investigation, setInvestigation] = useState(c.investigation_summary ?? '');
  const [vigRationale, setVigRationale] = useState(c.vigilance_rationale ?? '');
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(() => {
    fetchCaseEvents(c.id).then(setEvents).catch(() => setEvents([]));
    fetchFollowers(c.id).then(setFollowers).catch(() => setFollowers([]));
    if (c.nonconformance_id) fetchNcRef(c.nonconformance_id).then(setNcRef).catch(() => null);
  }, [c.id, c.nonconformance_id]);
  useEffect(() => { reload(); }, [reload]);

  const isComplaint = c.kind === 'complaint' || c.kind === 'non_conformity';

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer su-drawer">
        <h2>{c.number} · {c.title}</h2>
        {err && <p className="block-note">{err}</p>}
        <p className="muted">
          {label(CASE_KINDS, c.kind)} · {c.severity} · via {c.source.replace('_', ' ')} ·
          {' '}{accountName || c.contact_name || 'no account'}
          {c.contact_email && <> · {c.contact_email}</>}
          {' '}· opened by {memberName(c.created_by) || (c.portal_user_id ? (c.contact_name ?? 'the customer') : 'unknown')}
          {' '}{fmtDT(c.opened_at)}
        </p>
        {c.followup_of_case_id && (
          <p className="muted">
            Follow-up to{' '}
            <button className="link-btn" onClick={() => onOpenCase(c.followup_of_case_id!)}>
              {allCases.find((x) => x.id === c.followup_of_case_id)?.number ?? 'the original case'}
            </button>
          </p>
        )}
        {allCases.filter((x) => x.followup_of_case_id === c.id).map((fu) => (
          <p className="muted" key={fu.id}>
            Continued in <button className="link-btn" onClick={() => onOpenCase(fu.id)}>{fu.number}</button>
          </p>
        ))}
        {closed && (
          <p className="block-note">
            This case is closed and immutable. To continue the matter, raise a follow-up
            case below; it links back here and carries the context across.
          </p>
        )}

        {!closed && (
        <>
        <div className="su-two">
          <label className="cf-field"><span>Status</span>
            <select className="input sel" value={c.status}
                    onChange={(e) => {
                      const v = e.target.value;
                      updateCase(c.id, { status: v } as Partial<SupportCase>)
                        .then(() => { notifyCase(c.id, `status changed to ${v.replace(/_/g, ' ')}`); onChanged(); })
                        .catch((x) => setErr(x.message));
                    }}>
              {CASE_STATUSES.filter(([k]) => k !== 'closed').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label className="cf-field"><span>Severity</span>
            <select className="input sel" value={c.severity}
                    onChange={(e) => updateCase(c.id, { severity: e.target.value } as Partial<SupportCase>)
                      .then(onChanged).catch((x) => setErr(x.message))}>
              {CASE_SEVERITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
        </div>
        <div className="su-two">
          <label className="cf-field"><span>Assigned to</span>
            <select className="input sel" value={c.assigned_to ?? ''}
                    onChange={(e) => {
                      const v = e.target.value || null;
                      updateCase(c.id, { assigned_to: v } as Partial<SupportCase>)
                        .then(() => { if (v) notifyCase(c.id, `assigned to ${memberName(v)}`);
                          onMsg(v ? `Assigned to ${memberName(v)}.` : 'Unassigned.'); onChanged(); })
                        .catch((x) => setErr(x.message));
                    }}>
              <option value="">Unassigned</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
            </select></label>
          <label className="cf-field"><span>Product</span>
            <select className="input sel" value={c.product_id ?? ''}
                    onChange={(e) => updateCase(c.id, { product_id: e.target.value || null } as Partial<SupportCase>)
                      .then(onChanged).catch((x) => setErr(x.message))}>
              <option value="">None</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
            </select></label>
        </div>
        <label className="cf-field"><span>Lot</span>
          <select className="input sel" value={c.lot_id ?? ''}
                  onChange={(e) => updateCase(c.id, { lot_id: e.target.value || null } as Partial<SupportCase>)
                    .then(onChanged).catch((x) => setErr(x.message))}>
            <option value="">None</option>
            {stock.filter((sr) => !c.product_id || sr.product_id === c.product_id)
              .map((sr) => <option key={sr.lot_id} value={sr.lot_id}>{sr.sku} lot {sr.lot_number}</option>)}
          </select></label>
        </>
        )}

        {c.description && <p className="su-desc">{c.description}</p>}

        {isComplaint && (
          <section className="su-section">
            <h3>Investigation and reportability</h3>
            {closed ? (
              c.investigation_summary && <p className="su-desc">{c.investigation_summary}</p>
            ) : (
            <label className="cf-field"><span>Investigation summary</span>
              <textarea className="input" rows={3} value={investigation}
                        onChange={(e) => setInvestigation(e.target.value)}
                        onBlur={() => updateCase(c.id, { investigation_summary: investigation || null } as Partial<SupportCase>)
                          .then(onChanged).catch((x) => setErr(x.message))} /></label>
            )}
            {c.vigilance_assessed_at ? (
              <p className="su-vig">
                Reportability decided {fmtDT(c.vigilance_assessed_at)}:{' '}
                <strong>{c.vigilance_reportable ? 'REPORTABLE to the competent authority' : 'not reportable'}</strong>
                {c.vigilance_rationale && <> · {c.vigilance_rationale}</>}
              </p>
            ) : closed ? (
              <p className="muted">No reportability decision was recorded before closure.</p>
            ) : (
              <>
                <label className="cf-field"><span>Reportability rationale</span>
                  <input className="input" value={vigRationale} placeholder="Why this is, or is not, reportable"
                         onChange={(e) => setVigRationale(e.target.value)} /></label>
                <div className="su-vig-actions">
                  <button className="btn" onClick={() => {
                    if (!vigRationale.trim()) { setErr('The rationale is the record; write it.'); return; }
                    assessVigilance(c.id, false, vigRationale.trim())
                      .then(() => { onMsg('Recorded: not reportable.'); onChanged(); })
                      .catch((x) => setErr(x.message));
                  }}>Not reportable</button>
                  <button className="btn" onClick={() => {
                    if (!vigRationale.trim()) { setErr('The rationale is the record; write it.'); return; }
                    assessVigilance(c.id, true, vigRationale.trim())
                      .then(() => { onMsg('Recorded as REPORTABLE. Make the report and attach the reference.'); onChanged(); })
                      .catch((x) => setErr(x.message));
                  }}>Reportable</button>
                </div>
              </>
            )}
            {c.nonconformance_id ? (
              <p className="muted">Nonconformance {ncRef ?? ''} is linked in Quality; the CAPA chain lives there.</p>
            ) : closed ? null : (
              <button className="btn" onClick={() =>
                raiseNonconformance(c.id)
                  .then(() => { onMsg('Nonconformance raised and linked; Quality has it.'); onChanged(); })
                  .catch((x) => setErr(x.message))}>Raise nonconformance in Quality</button>
            )}
          </section>
        )}

        <section className="su-section">
          <div className="su-sec-row">
            <h3>Followers</h3>
            {!closed && (
              <select className="input sel su-addfollow" value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        addFollower(c.id, e.target.value)
                          .then(() => { onMsg('Follower added; they now get updates in Chorus and by email.'); reload(); })
                          .catch((x) => setErr(x.message));
                      }}>
                <option value="">Add a follower…</option>
                {members.filter((m) => !followers.some((fo) => fo.member_id === m.id))
                  .map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
            )}
          </div>
          {followers.length === 0 && <p className="muted">Nobody follows this case beyond the assignee and the opener.</p>}
          {followers.map((fo) => (
            <span key={fo.id} className="pill mute su-follower">
              {memberName(fo.member_id)}
              {!closed && (
                <button className="su-unfollow" title="Remove"
                        onClick={() => removeFollower(fo.id).then(reload).catch((x) => setErr(x.message))}>×</button>
              )}
            </span>
          ))}
        </section>

        <section className="su-section">
          <h3>Conversation</h3>
          {events.length === 0 && <p className="muted">Nothing yet beyond the opening.</p>}
          {events.map((e) => (
            <div key={e.id} className={`su-event ${e.visible_to_customer ? 'pub' : ''}`}>
              <div className="su-event-head">
                {e.portal_user_id
                  ? `${c.contact_name ?? 'Customer'} (customer)`
                  : memberName(e.created_by) || 'System'}
                {' '}· {e.kind === 'reply_to_customer' ? 'reply to customer'
                  : e.kind === 'escalation' ? 'escalation'
                  : e.kind === 'customer_message' ? 'message' : 'internal note'}
                {' '}· {fmtDT(e.created_at)}
                {e.visible_to_customer && <span className="pill info">customer sees this</span>}
              </div>
              <div className="su-event-body">{e.body}</div>
            </div>
          ))}
          {!closed && (
          <div className="su-addnote">
            <textarea className="input" rows={2} value={note}
                      placeholder="Write to the record: internal by default, tick the box to reply to the customer…"
                      onChange={(e) => setNote(e.target.value)} />
            <div className="su-addnote-row">
              <label className="lv-check">
                <input type="checkbox" checked={toCustomer}
                       onChange={(e) => setToCustomer(e.target.checked)} />
                <span>Visible to the customer on the portal</span>
              </label>
              <button className="btn primary" onClick={() => {
                if (!note.trim()) return;
                const body = note.trim();
                addCaseEvent(c.id, body, toCustomer)
                  .then(() => { setNote(''); setToCustomer(false);
                    notifyCase(c.id, `${toCustomer ? 'reply to customer' : 'note'}: ${body.slice(0, 120)}`);
                    reload(); })
                  .catch((x) => setErr(x.message));
              }}>Add</button>
            </div>
          </div>
          )}
        </section>

        <section className="su-section">
          <h3>Resolution</h3>
          {closed ? (
            <p className="su-desc">{c.resolution ?? 'No resolution text was recorded.'}</p>
          ) : (
          <label className="cf-field"><span>How it was resolved</span>
            <textarea className="input" rows={2} value={resolution}
                      onChange={(e) => setResolution(e.target.value)} /></label>
          )}
          <div className="exp-decide-actions">
            {!['resolved', 'closed'].includes(c.status) && (
              <button className="btn primary" onClick={() => {
                if (!resolution.trim()) { setErr('Write the resolution; it is the point of the record.'); return; }
                updateCase(c.id, { status: 'resolved', resolution: resolution.trim() } as Partial<SupportCase>)
                  .then(() => { notifyCase(c.id, `resolved: ${resolution.trim().slice(0, 120)}`); onMsg(`${c.number} resolved.`); onChanged(); })
                  .catch((x) => setErr(x.message));
              }}>Mark resolved</button>
            )}
            {c.status === 'resolved' && (
              <button className="btn primary" onClick={() => {
                if (!window.confirm('Close the case? A closed case is immutable; continuation happens in a follow-up case.')) return;
                updateCase(c.id, { status: 'closed' } as Partial<SupportCase>)
                  .then(() => { notifyCase(c.id, 'case closed'); onMsg(`${c.number} closed and sealed.`); onChanged(); })
                  .catch((x) => setErr(x.message));
              }}>Close the case</button>
            )}
            {['resolved', 'closed'].includes(c.status) && (
              <button className="btn" onClick={() =>
                createFollowupCase(c.id)
                  .then((id) => { onMsg('Follow-up case raised, linked to this one.'); onChanged(); onOpenCase(id); })
                  .catch((x) => setErr(x.message))}>Raise follow-up case</button>
            )}
            <button className="btn" onClick={onClose}>Done</button>
          </div>
        </section>
      </div>
    </>
  );
}
